import { ReactNode } from 'react'
import { Button as ReactAriaButton, ButtonProps } from 'react-aria-components'
import { useTranslation } from 'react-i18next'

import { CircularProgress, Flex } from 'app/ui'

import { joinStyles } from 'app/utils/theme'

import { ColorButton, SizeButton, VariantButton } from 'app/types/ui/button'
import * as styles from './index.css'

type ButtonProperties = ButtonProps & {
    className?: string
    variant?: VariantButton
    color?: ColorButton
    size?: SizeButton
    icon?: ReactNode
}

export const Button = ({
    children,
    className,
    variant = 'contained',
    color = 'primary',
    size = 'medium',
    icon,
    ...properties
}: Readonly<ButtonProperties>): JSX.Element => {
    const { t } = useTranslation()

    return (
        <ReactAriaButton
            className={joinStyles(
                styles[variant][color],
                variant !== VariantButton.enum.icon && styles.size[size],
                variant === VariantButton.enum.icon && styles.sizeButton[size],
                className,
            )}
            {...properties}
        >
            {({ isPending }) => (
                <>
                    {!!isPending && (
                        <CircularProgress
                            className={styles.circularProgress}
                            aria-label={t('pending')}
                            isIndeterminate
                        />
                    )}

                    {!!icon && !isPending && (
                        <Flex
                            className={joinStyles(
                                styles.iconButton,
                                children && styles.iconButtonMargin,
                            )}
                        >
                            {icon}
                        </Flex>
                    )}

                    {children}
                </>
            )}
        </ReactAriaButton>
    )
}
